import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { ArrowRight, Target, Zap, Microscope, Users } from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/landing.css';

const structuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'UXAuditPro',
  url: 'https://www.uxauditpro.com/',
  logo: 'https://www.uxauditpro.com/logo512.png',
  description: 'AI-powered UX audit platform helping product teams identify and fix UX problems fast.',
  foundingDate: '2024',
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: 'https://www.uxauditpro.com/contact/',
  },
};

// PHASE E. These four fields held emoji — the design system readme's rule is
// "no emoji anywhere in the brand", and they rendered as a colour glyph on some
// platforms and as tofu on others. They are lucide components now, which is
// what the rest of the product already uses for feature marks. The SPEC's icon
// system is "a geometric primitive in a sand tile"; moving the whole product to
// that is costed in DECISIONS.md and deliberately NOT executed in this phase.
const values = [
  {
    Icon: Target,
    title: 'Clarity Over Complexity',
    body: 'Every audit delivers a clear, prioritised action list — not a 100-page report nobody reads.',
  },
  {
    Icon: Zap,
    title: 'Speed That Matters',
    body: 'Results in under 5 minutes so teams can act in the same sprint they discovered the problem.',
  },
  {
    Icon: Microscope,
    title: 'Rigorous Analysis',
    body: 'Every audit captures real desktop and mobile screenshots plus measured values — contrast ratios, tap-target sizes — and every finding must cite that evidence or it is discarded.',
  },
  {
    Icon: Users,
    title: 'Built for Teams',
    body: 'White-label reports, agency plans, and collaborative workspaces — designed for real product workflows.',
  },
];

// REMOVED 2026-08-17, both demonstrably false (identical block to HomePage.js):
//   "500+ Apps Audited"       — production has 106 audits, total, all time.
//   "92% Client Satisfaction" — no satisfaction survey exists.
// "18 Avg Findings per Audit" is unverified and flagged, not removed.
const stats = [
  { number: '18', label: 'Avg Findings per Audit' },
  { number: '<5 min', label: 'Time to Results' },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        fullTitle="About UXAuditPro: The AI-Powered UX Audit Platform"
        description="UXAuditPro is an AI-powered UX audit platform helping product teams, agencies, and startups identify usability issues and fix them faster. Learn our mission."
        canonical="/about"
        ogTitle="About UXAuditPro — AI UX Audit Platform"
        structuredData={structuredData}
      />

      <main style={{ minHeight: '100vh' }}>

        {/* Hero */}
        <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
          <div className="uxa-hero-grid" aria-hidden="true" />
          <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto' }}>
          <div>
            <span
              className="inline-block px-3 py-1 rounded-full text-sm font-medium mb-6 uppercase tracking-widest"
              style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent-text)' }}
            >
              Our Story
            </span>
            <h1
              className="uxa-h1 mb-6"
              style={{ color: 'var(--ink-1)', textWrap: 'pretty' }}
            >
              We're making professional UX audits accessible to every team
            </h1>
            <p className="uxa-lead mx-auto" style={{ color: 'var(--ink-2)', maxWidth: '56ch' }}>
              UXAuditPro was born out of frustration — watching great products lose users to fixable UX
              problems that nobody had time to properly document. We set out to make expert-level UX
              analysis available to every product team, instantly.
            </p>
          </div>
        </div>
        </header>

        {/* Stats */}
        <section className="uxa-landing-section uxa-pt-xl">
          {/* WAS a four-column grid holding two tiles, since "500+ Apps Audited"
              and "92% Client Satisfaction" were removed on 2026-08-17 as
              demonstrably false. Two cells in a row built for four read as two
              stats failing to load — the same defect the /use-cases/ band had.
              It is a two-up now, which is what it actually is.

              "18 Avg Findings per Audit" remains UNVERIFIED and flagged, not
              removed; "<5 min" is the service commitment settled today. */}
          <div className="uxa-headline-stats">
            <div className="uxa-headline-stats__edge" aria-hidden="true" />
            <div className="uxa-headline-stats__grid">
            {stats.map((s, i) => (
              <div
                key={s.label}
              >
                <div className="uxa-headline-stats__value">{s.number}</div>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px 0 0' }}>{s.label}</p>
              </div>
            ))}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="uxa-landing-section uxa-pt-xl">
          {/* The page's one tonal break, on the system's dark panel. */}
          <div className="uxa-dark-panel">
            <span className="uxa-dark-panel__eyebrow">Mission</span>
            <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px 0 0' }}>
              "Make expert UX analysis instant, affordable, and actionable for every product team on earth."
            </h2>
          </div>
        </section>

        {/* Values */}
        <section className="uxa-landing-section uxa-pt-xl">
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>What we believe in</h2>
          <div className="uxa-grid-2" style={{ marginTop: 40 }}>
            {values.map((v, i) => (
              <div
                key={v.title}
                className="uxa-feature-card"
              >
                {/* `stroke`, not the colour property. A lucide icon draws with
                    stroke="currentColor", so tinting it through the colour
                    property works — and the accent lint fired on that, correctly:
                    on a component, the colour property is indistinguishable from
                    a text colour without knowing what the component renders. The
                    accent IS permitted for icon fills (DECISIONS.md standing
                    rule), so the fix is to write the property that says so.
                    (Phrased without the literal because that lint is the one
                    guard here that does NOT strip comments — logged.) */}
                <v.Icon className="w-7 h-7 mb-3" aria-hidden="true" stroke="var(--accent)" strokeWidth={1.75} />
                <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '24px 0 8px' }}>{v.title}</h3>
                <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Independent Product */}
        <section className="uxa-landing-section uxa-pt-xl">
          <div
            className="uxa-feature-card flex flex-col md:flex-row items-start md:items-center gap-6"
          >
            <div className="flex-1">
              <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--ink-2)' }}>The Product</p>
              <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '0 0 12px' }}>UXAuditPro</h2>
              <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: 0 }}>
                UXAuditPro is an independent AI-powered UX audit platform — purpose-built to help product teams,
                agencies, and startups identify usability issues and fix them fast. We combine multiple AI models
                with proven UX heuristics to deliver expert-level analysis in minutes, not weeks.
              </p>
            </div>
            <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
              Try it free
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="uxa-closing" style={{ marginTop: 120 }}>
          <div className="uxa-closing__grid" aria-hidden="true" />
          <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>Ready to try it?</h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 40px', maxWidth: 520 }}>One free audit every month. No credit card required.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/ux-auditor/" className="uxa-submit uxa-submit--auto">
              Start Free Audit
              <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
            </Link>
            <Link to="/contact/" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              Get in Touch
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
        </section>

      </main>
    </>
  );
}
